import { useCallback, useEffect, useRef, useState } from 'react';
import { playQuietly, prefersReducedMotion, useInView } from './hooks';
import StoreButtons from './StoreButtons';
import './HomeFeatures.css';

/*
 * Deck clips: content area of the app (below the race header, above the tab
 * bar), 720x932, 6 s, H.264 MP4 + VP9 WebM, no audio, first-frame JPEG poster.
 * They play once per card; when one ends the deck moves to the next card.
 */
const CLIP = (name) => ({
  mp4: `/videos/app/${name}.mp4`,
  webm: `/videos/app/${name}.webm`,
  poster: `/videos/app/${name}.jpg`,
});

export const TONES = {
  violet: { color: '#7c3aed', tint: '#f5f3ff', deep: '#4c1d95' },
  green: { color: '#059669', tint: '#ecfdf5', deep: '#065f46' },
  pink: { color: '#db2777', tint: '#fdf2f8', deep: '#9d174d' },
  amber: { color: '#d97706', tint: '#fffbeb', deep: '#92400e' },
};

export const FEATURES = [
  {
    id: 'racecard',
    tone: 'violet',
    short: 'Racecard',
    eyebrow: 'Every race, analysed',
    title: 'Racecards that cut through the noise.',
    copy: 'Filter runners, compare every horse at a glance and let runner highlights flag the trends that matter, instantly.',
    clip: CLIP('deck-racecard'),
  },
  {
    id: 'form',
    tone: 'green',
    short: 'Form',
    eyebrow: 'Form & performance',
    title: 'Every run, and whether the form has held up.',
    copy: "Each horse's previous races with the result, going and distance, plus whether the form has been franked by rivals winning or rising in the ratings since. Performance tables then show how it fares over this distance, class and course.",
    clip: CLIP('deck-form-performance'),
  },
  {
    id: 'stats',
    tone: 'pink',
    short: 'Stats',
    eyebrow: 'Connections & breeding',
    title: '22 analysis tables. Every angle covered.',
    copy: 'Trainer, jockey and owner records at this course and distance, plus sire and dam stats by going, class and distance, for every runner in the race.',
    clip: CLIP('deck-stats'),
  },
  {
    id: 'rating',
    tone: 'amber',
    short: 'Rating',
    eyebrow: 'Predictive ratings',
    title: 'Ratings built from thousands of simulations.',
    copy: 'Every race is simulated across 22 variables, 10,000 times, to give each horse a predicted finishing position you can act on.',
    clip: CLIP('deck-rating'),
  },
];

// 24 Sep 16:45 Newmarket, top five by HorseStat Rating (from the app), in cloth-number order.
// `share` only shapes the illustrative win percentages during the run.
export const SIM_RUNNERS = [
  { no: 1, name: 'Cancan In The Rain', jockey: 'George Wood', rating: 240, silk: '/images/silks/cancan.png', share: 0.29 },
  { no: 3, name: 'Signcastle City', jockey: 'Joe Leavy', rating: 208, silk: '/images/silks/signcastle.png', share: 0.07 },
  { no: 4, name: 'Potomac River', jockey: 'Ray Dawson', rating: 235, silk: '/images/silks/potomac.png', share: 0.22 },
  { no: 9, name: 'Berry Clever', jockey: 'Silvestre De Sousa', rating: 241, silk: '/images/silks/berry.png', share: 0.31 },
  { no: 12, name: 'Born A Rebel', jockey: 'Benoit de la Sayette', rating: 216, silk: '/images/silks/rebel.png', share: 0.11 },
];

export function formatStat(value) {
  return Math.round(value).toLocaleString('en-GB');
}

const IDLE_AFTER_TOUCH_MS = 7000;

function FeatureDeck() {
  const deckRef = useRef(null);
  const cardRefs = useRef([]);
  const videoRefs = useRef([]);
  const fillRefs = useRef([]);
  const lastTouch = useRef(0);
  const [active, setActive] = useState(0);
  const [wrapRef, visible] = useInView({ threshold: 0.45, once: false });

  const scrollToCard = useCallback((index, behavior = 'smooth') => {
    const deck = deckRef.current;
    const card = cardRefs.current[index];
    if (!deck || !card || typeof deck.scrollTo !== 'function') return;
    deck.scrollTo({ left: card.offsetLeft - deck.offsetLeft - parseFloat(getComputedStyle(deck).paddingLeft), behavior: prefersReducedMotion() ? 'auto' : behavior });
  }, []);

  const onScroll = () => {
    const deck = deckRef.current;
    if (!deck) return;
    const centre = deck.scrollLeft + deck.clientWidth / 2;
    let nearest = 0;
    cardRefs.current.forEach((card, index) => {
      if (!card) return;
      const cardCentre = card.offsetLeft - deck.offsetLeft + card.offsetWidth / 2;
      const best = cardRefs.current[nearest];
      const bestCentre = best.offsetLeft - deck.offsetLeft + best.offsetWidth / 2;
      if (Math.abs(cardCentre - centre) < Math.abs(bestCentre - centre)) nearest = index;
    });
    setActive(nearest);
  };

  // Active card plays from the start; the rest wait.
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === active && visible) {
        playQuietly(video);
      } else {
        video.pause();
        if (index !== active) video.currentTime = 0;
      }
    });
  }, [active, visible]);

  // Story-style progress in the active pill.
  useEffect(() => {
    if (typeof requestAnimationFrame === 'undefined') return undefined;
    let frame = 0;
    const tick = () => {
      fillRefs.current.forEach((fill, index) => {
        if (!fill) return;
        const video = videoRefs.current[index];
        const amount = index === active && video && video.duration ? video.currentTime / video.duration : 0;
        fill.style.transform = `scaleX(${amount})`;
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active]);

  const onEnded = (index) => {
    if (index !== active) return;
    if (Date.now() - lastTouch.current < IDLE_AFTER_TOUCH_MS) {
      playQuietly(videoRefs.current[index]);
      return;
    }
    scrollToCard((index + 1) % FEATURES.length);
  };

  const pick = (index) => {
    lastTouch.current = Date.now();
    scrollToCard(index);
  };

  return (
    <div ref={wrapRef} className="toolkit-deck-wrap">
      <div className="toolkit-pills" role="tablist" aria-label="Features">
        {FEATURES.map((feature, index) => (
          <button
            key={feature.id}
            type="button"
            role="tab"
            aria-selected={index === active}
            className={index === active ? 'is-active' : ''}
            style={{ '--toolkit-tone': TONES[feature.tone].color }}
            onClick={() => pick(index)}
          >
            <span className="toolkit-pills__label">{feature.short}</span>
            <span className="toolkit-pills__track">
              <span ref={(node) => { fillRefs.current[index] = node; }} className="toolkit-pills__fill" />
            </span>
          </button>
        ))}
      </div>

      <div
        ref={deckRef}
        className="toolkit-deck"
        onScroll={onScroll}
        onTouchStart={() => { lastTouch.current = Date.now(); }}
        onPointerDown={() => { lastTouch.current = Date.now(); }}
      >
        {FEATURES.map((feature, index) => {
          const tone = TONES[feature.tone];
          return (
            <article
              key={feature.id}
              ref={(node) => { cardRefs.current[index] = node; }}
              className={`toolkit-card${index === active ? ' is-active' : ''}`}
              style={{ '--toolkit-tone': tone.color, '--toolkit-tint': tone.tint, '--toolkit-deep': tone.deep }}
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${FEATURES.length}: ${feature.short}`}
            >
              <div className="toolkit-card__text">
                <p className="toolkit-card__eyebrow">{feature.eyebrow}</p>
                <h3>{feature.title}</h3>
                <p className="toolkit-card__copy">{feature.copy}</p>
              </div>
              <div className="toolkit-card__device" aria-hidden="true">
                <video
                  ref={(node) => { videoRefs.current[index] = node; }}
                  muted
                  playsInline
                  preload={index === 0 ? 'auto' : 'metadata'}
                  poster={feature.clip.poster}
                  onEnded={() => onEnded(index)}
                >
                  <source src={feature.clip.webm} type="video/webm" />
                  <source src={feature.clip.mp4} type="video/mp4" />
                </video>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

const ROW_H = 64;
const SIM_MS = 2600;
const RANK_CLASS = ['is-gold', 'is-silver', 'is-bronze'];

export function SimWidget({ showRun = true }) {
  const [phase, setPhase] = useState('idle'); // idle | running | done
  const [count, setCount] = useState(0);
  const [shares, setShares] = useState(() => SIM_RUNNERS.map(() => 0));
  const [ref, inView] = useInView({ threshold: 0.6 });
  const frameRef = useRef(0);
  const doneTimer = useRef(0);
  const autoRan = useRef(false);

  const run = useCallback(() => {
    cancelAnimationFrame(frameRef.current);
    setPhase('running');
    if (prefersReducedMotion()) {
      setCount(10000);
      setShares(SIM_RUNNERS.map((runner) => runner.share));
      setPhase('done');
      return;
    }
    const noise = SIM_RUNNERS.map(() => Math.random());
    const start = performance.now();
    let last = 0;
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / SIM_MS);
      if (now - last > 45 || progress === 1) {
        last = now;
        const settle = Math.pow(progress, 0.8);
        setCount(10000 * (1 - Math.pow(1 - progress, 2)));
        setShares(
          SIM_RUNNERS.map((runner, index) => {
            noise[index] = Math.min(1, Math.max(0, noise[index] + (Math.random() - 0.5) * 0.35));
            const wobble = (noise[index] - 0.5) * 0.24 * (1 - settle);
            return Math.max(0.01, runner.share * settle + (0.2 + wobble) * (1 - settle));
          })
        );
      }
      if (progress < 1) frameRef.current = requestAnimationFrame(tick);
      else doneTimer.current = window.setTimeout(() => setPhase('done'), 250);
    };
    frameRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => () => {
    cancelAnimationFrame(frameRef.current);
    window.clearTimeout(doneTimer.current);
  }, []);

  useEffect(() => {
    if (!inView || autoRan.current) return undefined;
    autoRan.current = true;
    const timer = window.setTimeout(run, 500);
    return () => window.clearTimeout(timer);
  }, [inView, run]);

  const byRating = [...SIM_RUNNERS].map((runner, index) => ({ runner, index })).sort((a, b) => b.runner.rating - a.runner.rating);
  const rankOf = (index) => byRating.findIndex((entry) => entry.index === index);
  const maxShare = Math.max(...shares, 0.0001);

  return (
    <section ref={ref} className={`toolkit-sim toolkit-sim--${phase}`} aria-label="How the HorseStat Rating works">
      <p className="mobile-eyebrow">Predictive ratings</p>
      <h2>Watch a race get rated.</h2>
      <p className="toolkit-sim__copy">
        Every race is simulated 10,000 times across 22 variables. The runners that win most often earn the highest HorseStat Rating.
      </p>

      <div className="toolkit-sim__card">
        <div className="toolkit-sim__head">
          <span>24 Sep · 16:45 Newmarket</span>
          <span className="toolkit-sim__count" aria-live="polite">
            {formatStat(count)} <small>sims</small>
          </span>
        </div>

        <ol className="toolkit-sim__rows" style={{ height: SIM_RUNNERS.length * ROW_H }}>
          {SIM_RUNNERS.map((runner, index) => {
            const rank = rankOf(index);
            const position = phase === 'done' ? rank : index;
            return (
              <li
                key={runner.name}
                className={`toolkit-sim__row ${phase === 'done' ? RANK_CLASS[rank] || '' : ''}`}
                style={{ transform: `translateY(${position * ROW_H}px)`, height: ROW_H }}
              >
                <img src={runner.silk} alt="" className="toolkit-sim__silk" />
                <div className="toolkit-sim__who">
                  <strong>{runner.no}. {runner.name}</strong>
                  <span>J: {runner.jockey}</span>
                  <span className="toolkit-sim__bar">
                    <span style={{ transform: `scaleX(${shares[index] / maxShare})` }} />
                  </span>
                </div>
                <span className="toolkit-sim__badge">{phase === 'done' ? runner.rating : `${Math.round(shares[index] * 100)}%`}</span>
              </li>
            );
          })}
        </ol>

        {showRun ? (
          <button type="button" className="toolkit-sim__run" onClick={run} disabled={phase === 'running'}>
            {phase === 'running' ? 'Simulating…' : phase === 'done' ? 'Run it again' : 'Run 10,000 simulations'}
          </button>
        ) : null}
      </div>
      <p className="toolkit-sim__note">Real HorseStat Ratings for this race. Win percentages during the run are illustrative.</p>
    </section>
  );
}

/* Mobile homepage features: swipeable deck with synced pills, then an interactive rating demo. */
function HomeFeatures() {
  return (
    <section className="toolkit" aria-label="What HorseStat does">
      <header className="toolkit-intro">
        <p className="mobile-eyebrow">Everything in one place</p>
        <h2>Swipe through the toolkit.</h2>
      </header>
      <FeatureDeck />
      <SimWidget showRun={false} />
      <div className="toolkit-download">
        <p>Get the HorseStat Rating for every race. Free to start on iPhone and Android.</p>
        <StoreButtons />
      </div>
    </section>
  );
}

export default HomeFeatures;
